import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { formatUserLevel } from '@/utils/formatters';
import { cn } from '@/lib/utils';
import { adminService, SystemOverview, SystemHealth, AuditLog } from '@/services/adminService';
import { diagnosisService } from '@/services/diagnosisService';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { PatientForm } from '@/components/forms/PatientForm';
import {
  Users,
  Hospital,
  Activity,
  FileText,
  Calendar,
  Globe,
  Shield,
  Database,
  BarChart3,
  Settings,
  AlertTriangle,
  Stethoscope,
  Heart,
  PlusCircle,
  Maximize2,
  Minimize2,
  UserPlus,
  X,
  Baby
} from 'lucide-react';

export const AdministratorDashboard: React.FC = () => {
  const { utilizator } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [systemOverview, setSystemOverview] = useState<SystemOverview | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [recentActivityData, setRecentActivityData] = useState<AuditLog[]>([]);
  const [eurocatCompliance, setEurocatCompliance] = useState(0);
  const [diagnosisCount, setDiagnosisCount] = useState<number>(0);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setError(null);
      const [overview, health, activity, diagnoses] = await Promise.all([
        adminService.getSystemOverview(),
        adminService.getSystemHealth(),
        adminService.getRecentActivity(5),
        diagnosisService.getAllDiagnoses().catch(() => [])
      ]);

      setSystemOverview(overview);
      setSystemHealth(health);
      setRecentActivityData(activity);
      // Ensure diagnoses is an array before accessing length
      if (Array.isArray(diagnoses)) {
        setDiagnosisCount(diagnoses.length);
      } else if (diagnoses && typeof diagnoses === 'object' && 'data' in diagnoses) {
        const diagnosesWithData = diagnoses as { data: any[] };
        setDiagnosisCount(Array.isArray(diagnosesWithData.data) ? diagnosesWithData.data.length : 0);
      } else {
        setDiagnosisCount(0);
      }

      // Calculate EUROCAT compliance (mock for now)
      const compliance = overview.diagnoses > 0
        ? Math.round((overview.diagnoses / overview.patients) * 100)
        : 0;
      setEurocatCompliance(Math.min(compliance, 100));

      setLoading(false);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Eroare la încărcarea datelor');
      setLoading(false);
    }
  };

  if (!utilizator) {
    return null;
  }

  const systemStats = [
    {
      title: 'Total utilizatori',
      value: systemOverview?.users?.total?.toString() || '0',
      change: (systemOverview?.users?.total || 0) > 0 ? '+' + Math.round(((systemOverview?.users?.total || 0) / 10) * 100) + '%' : '+0%',
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Spitale active',
      value: systemOverview?.hospitals?.toString() || '0',
      change: (systemOverview?.hospitals || 0) > 0 ? '+' + Math.round(((systemOverview?.hospitals || 0) / 5) * 100) + '%' : '+0%',
      icon: Hospital,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Diagnostice MCC',
      value: (diagnosisCount || 0).toString(),
      change: diagnosisCount > 0 ? '+' + Math.round((diagnosisCount / 50) * 100) + '%' : '+0%',
      icon: Heart,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Conformitate EUROCAT',
      value: eurocatCompliance + '%',
      change: eurocatCompliance > 50 ? '+' + Math.round(eurocatCompliance / 10) + '%' : '+0%',
      icon: Globe,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      title: 'Copii prematuri',
      value: (systemOverview?.premature_cases || 0).toString(),
      change: (systemOverview?.patients || 0) > 0 ? `${((systemOverview?.premature_cases || 0) / (systemOverview?.patients || 1) * 100).toFixed(1)}%` : '0%',
      icon: Baby,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
    },
  ];

  const adminActions = [
    {
      title: 'Caz nou',
      description: 'Adaugă un nou caz MCC identificat',
      icon: PlusCircle,
      action: 'new-case',
      color: 'bg-blue-500',
    },
    {
      title: 'Gestionare utilizatori',
      description: 'Adaugă, modifică sau șterge utilizatori',
      icon: Users,
      action: 'users',
      color: 'bg-green-500',
    },
    {
      title: 'Gestionare spitale',
      description: 'Administrează unitățile medicale',
      icon: Hospital,
      action: 'hospitals',
      color: 'bg-purple-500',
    },
    {
      title: 'Diagnostice MCC',
      description: 'Gestionare diagnostice sistem',
      icon: Heart,
      action: 'diagnoses',
      color: 'bg-red-500',
    },
    {
      title: 'Coduri MCC',
      description: 'Gestionează codurile ICD-10 pentru MCC',
      icon: Activity,
      action: 'chd-codes',
      color: 'bg-pink-500',
    },
    {
      title: 'Tipuri proceduri',
      description: 'Administrează tipurile de proceduri medicale',
      icon: Stethoscope,
      action: 'procedure-types',
      color: 'bg-teal-500',
    },
    {
      title: 'Rapoarte EUROCAT',
      description: 'Generează și trimite rapoarte',
      icon: Globe,
      action: 'eurocat-reports',
      color: 'bg-indigo-500',
    },
    {
      title: 'Analize sistem',
      description: 'Statistici la nivel național',
      icon: BarChart3,
      action: 'system-analytics',
      color: 'bg-orange-500',
    },
    {
      title: 'Audit & Securitate',
      description: 'Monitorizează activitatea sistemului',
      icon: Shield,
      action: 'audit',
      color: 'bg-gray-500',
    },
    {
      title: 'Backup & Restore',
      description: 'Gestionează copiile de siguranță',
      icon: Database,
      action: 'backup',
      color: 'bg-yellow-500',
    },
    {
      title: 'Configurări sistem',
      description: 'Setări generale ale aplicației',
      icon: Settings,
      action: 'settings',
      color: 'bg-slate-500',
    },
    {
      title: 'Alerte sistem',
      description: 'Monitorizează problemele',
      icon: AlertTriangle,
      action: 'alerts',
      color: 'bg-red-500',
    },
  ];

  const formatActivityMessage = (log: AuditLog) => {
    const actionMap: Record<string, string> = {
      'INSERT': 'Adăugat',
      'UPDATE': 'Modificat',
      'DELETE': 'Șters',
      'LOGIN': 'Autentificare',
      'LOGOUT': 'Deconectare',
      'CREATE': 'Creat',
      'READ': 'Vizualizat',
      'VIEW': 'Vizualizat'
    };

    const tableMap: Record<string, string> = {
      'users': 'utilizator',
      'patients': 'pacient',
      'hospitals': 'spital',
      'chd_diagnoses': 'diagnostic MCC',
      'icd10_chd_codes': 'cod MCC',
      'procedures': 'procedură medicală'
    };

    const action = actionMap[log.action?.toUpperCase()] || log.action || 'Acțiune';
    const table = tableMap[log.table_name] || log.table_name || 'înregistrare';

    return `${action} ${table} - ${log.full_name || log.username}`;
  };

  const getActivityIcon = (tableName: string) => {
    switch (tableName) {
      case 'users': return Users;
      case 'hospitals': return Hospital;
      case 'patients': return Activity;
      case 'icd10_chd_codes': return Heart;
      default: return FileText;
    }
  };

  const getActivityColor = (tableName: string) => {
    switch (tableName) {
      case 'users': return 'text-blue-500';
      case 'hospitals': return 'text-green-500';
      case 'patients': return 'text-red-500';
      case 'icd10_chd_codes': return 'text-pink-500';
      default: return 'text-purple-500';
    }
  };

  const handleAdminAction = (action: string) => {
    const event = new CustomEvent('navigate', { detail: { page: action } });
    window.dispatchEvent(event);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16" />
                  <Skeleton className="h-3 w-32 mt-2" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <>
      <MainLayout>
        <div className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {/* Welcome Section */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                Tablou de Bord - Administrator
              </h1>
              <p className="text-muted-foreground">
                Bine ați venit, {utilizator.name || utilizator.username || 'utilizator'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">
                {formatUserLevel(utilizator.user_level?.id || utilizator.user_level_id)}
              </Badge>
              <Badge variant="destructive" className="text-xs">
                Acces complet
              </Badge>
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                {new Date().toLocaleDateString('ro-RO')}
              </Button>
              <Button size="sm">
                <FileText className="mr-2 h-4 w-4" />
                Export date
              </Button>
            </div>
          </div>

          {/* System Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {systemStats.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <div className={`rounded-full p-2 ${stat.bgColor}`}>
                      <Icon className={`h-4 w-4 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <p className="text-xs text-muted-foreground">
                      <span className="text-green-600">{stat.change}</span> față de luna trecută
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Admin Actions Grid */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Administrare sistem</h2>
            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {adminActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Card
                    key={action.action}
                    className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
                    onClick={() => handleAdminAction(action.action)}
                  >
                    <CardContent className="p-4">
                      <div className={`rounded-lg p-2 ${action.color} text-white w-fit mb-3`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-semibold mb-1 text-sm">{action.title}</h3>
                      <p className="text-xs text-muted-foreground">
                        {action.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Activitate recentă
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAdminAction('audit')}
                  >
                    Vezi tot
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentActivityData.length > 0 ? (
                    recentActivityData.slice(0, 5).map((log, index) => {
                      const Icon = getActivityIcon(log.table_name);
                      return (
                        <div key={log.audit_id || `activity-${index}`} className="flex items-start gap-3">
                          <div className="rounded-full p-1.5 bg-gray-100 flex-shrink-0">
                            <Icon className={`h-3 w-3 ${getActivityColor(log.table_name)}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{formatActivityMessage(log)}</p>
                            <p className="text-xs text-muted-foreground">
                              {log.action_timestamp && !isNaN(Date.parse(log.action_timestamp))
                                ? formatDistanceToNow(new Date(log.action_timestamp), {
                                  addSuffix: true,
                                  locale: ro
                                })
                                : 'Recent'}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-muted-foreground">Nu există activitate recentă</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* System Health */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-green-500" />
                  Sănătate sistem
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Server API</span>
                    <Badge
                      variant="default"
                      className={systemHealth?.api?.status === 'operational' ? 'bg-green-500' : 'bg-red-500'}
                    >
                      {systemHealth?.api?.status === 'operational' ? 'Operațional' : 'Probleme'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Bază de Date</span>
                    <Badge
                      variant="default"
                      className={systemHealth?.database?.status === 'operational' ? 'bg-green-500' : 'bg-red-500'}
                    >
                      {systemHealth?.database?.status === 'operational' ? 'Operațional' : 'Probleme'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Sincronizare EUROCAT</span>
                    <Badge variant="default" className="bg-green-500">Activ</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Backup automat</span>
                    <Badge
                      variant="default"
                      className={systemHealth?.backup?.status === 'scheduled' || systemHealth?.backup?.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'}
                    >
                      {systemHealth?.backup?.status === 'scheduled' ? 'Programat' :
                        systemHealth?.backup?.status === 'success' ? 'Reușit' :
                          systemHealth?.backup?.status === 'running' ? 'În curs' : 'Eșuat'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Utilizare stocare</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${(systemHealth?.storage?.percentage_used || 0) < 70 ? 'bg-green-500' :
                            (systemHealth?.storage?.percentage_used || 0) < 90 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                          style={{ width: `${systemHealth?.storage?.percentage_used || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {systemHealth?.storage?.percentage_used || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* EUROCAT Compliance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-purple-500" />
                Conformitate EUROCAT
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-3">
                <div>
                  <p className="text-sm text-muted-foreground">Ultimul raport trimis</p>
                  <p className="text-lg font-semibold">
                    {systemHealth?.backup?.last_backup
                      ? new Date(systemHealth.backup.last_backup).toLocaleDateString('ro-RO')
                      : 'Nu există'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Următorul raport</p>
                  <p className="text-lg font-semibold">
                    {systemHealth?.backup?.next_scheduled
                      ? new Date(systemHealth.backup.next_scheduled).toLocaleDateString('ro-RO')
                      : '30 Martie 2025'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cazuri de Raportat</p>
                  <p className="text-lg font-semibold">{systemOverview?.diagnoses || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    </>
  );
};
